import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  PasswordInput,
  Button,
  Title,
  Text,
  Container,
  Stack,
  Alert,
  Center,
  Box,
  Group,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconInfoCircle } from '@tabler/icons-react';
import { authService } from '../../services/api/auth';
import { SafeLoadIcon } from '../../components/shared';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters.';
    } else if (newPassword.length > 128) {
      newErrors.newPassword = 'Password must be 128 characters or fewer.';
    }

    if (!confirmNewPassword) {
      newErrors.confirmNewPassword = 'Please confirm your new password.';
    } else if (newPassword && confirmNewPassword !== newPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError('');
    try {
      await authService.resetPassword({
        token,
        newPassword,
        confirmNewPassword,
      });
      setSuccess(true);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    if (errors.newPassword) {
      setErrors(prev => { const next = { ...prev }; delete next.newPassword; return next; });
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmNewPassword(value);
    if (errors.confirmNewPassword) {
      setErrors(prev => { const next = { ...prev }; delete next.confirmNewPassword; return next; });
    }
  };

  if (!token) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#f8f9fa',
        }}
      >
        <Container size={420} style={{ width: '100%' }}>
          <Paper withBorder shadow="md" p={30} radius="md">
            <Stack gap="md" align="center">
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Invalid Link" w="100%">
                This password reset link is invalid. Please request a new one from the login page.
              </Alert>
              <Button variant="light" onClick={() => navigate('/login')}>
                Return to Login
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
      }}
    >
      <Container size={420} style={{ width: '100%' }}>
        <Center mb="xl">
          <Stack align="center" gap="xs">
            <SafeLoadIcon size={60} />
            <Title order={1} size="h2" ta="center" c="dark">
              SafeLoad Admin
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              Reset your password
            </Text>
          </Stack>
        </Center>

        <Paper withBorder shadow="md" p={30} radius="md">
          {success ? (
            <Stack gap="md">
              <Alert icon={<IconCheck size={16} />} color="green" title="Password Reset">
                Your password has been reset successfully. You may now log in with your new password.
              </Alert>
              <Button fullWidth onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </Stack>
          ) : (
            <Stack gap="md">
              <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                <Text size="sm">Password must be between 8 and 128 characters.</Text>
              </Alert>

              {apiError && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
                  {apiError}
                </Alert>
              )}

              <PasswordInput
                label="New Password"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={e => handleNewPasswordChange(e.currentTarget.value)}
                error={errors.newPassword}
                required
              />

              <PasswordInput
                label="Confirm New Password"
                placeholder="Re-enter your new password"
                value={confirmNewPassword}
                onChange={e => handleConfirmPasswordChange(e.currentTarget.value)}
                error={errors.confirmNewPassword}
                required
              />

              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => navigate('/login')}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} loading={loading}>
                  Reset Password
                </Button>
              </Group>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
